import {
  ApplicationRef,
  createComponent,
  EnvironmentInjector,
  inject,
  Injectable,
} from '@angular/core';
import {downloadElementAsPng} from '@gatewee/utils';
import {GateweePaymentReceiptExportCardComponent} from './gatewee-payment-receipt-export-card.component';
import {PaymentReceiptExportModel} from './payment-receipt-export.model';

/**
 * Renders {@link GateweePaymentReceiptExportCardComponent} off-screen, captures PNG, then destroys it.
 * Keeps export markup out of feature templates.
 */
@Injectable({providedIn: 'root'})
export class PaymentReceiptExportService {
  private readonly appRef = inject(ApplicationRef);
  private readonly environmentInjector = inject(EnvironmentInjector);

  /**
   * Renders the export card off-screen and either downloads a PNG or, when `debug` is true,
   * opens the prepared markup in a new browser tab (styles copied from the current page).
   */
  async downloadPng(
    model: PaymentReceiptExportModel,
    filename: string,
    debug = false,
  ): Promise<void> {
    const host = document.createElement('div');
    host.setAttribute('aria-hidden', 'true');
    host.style.cssText =
      'position:fixed;top:0;left:0;width:420px;z-index:-1;pointer-events:none;transform:translateX(-120vw);opacity:1;';
    document.body.appendChild(host);

    const componentRef = createComponent(
      GateweePaymentReceiptExportCardComponent,
      {
        hostElement: host,
        environmentInjector: this.environmentInjector,
      },
    );

    this.appRef.attachView(componentRef.hostView);

    try {
      componentRef.setInput('receipt', model.receipt);
      componentRef.setInput('amount', model.amount);
      componentRef.setInput(
        'requestedAmount',
        model.requestedAmount ?? model.amount,
      );
      componentRef.setInput('calculatedFee', model.calculatedFee ?? null);
      componentRef.setInput('finalAmount', model.finalAmount ?? null);
      componentRef.setInput('recipientDisplayName', model.recipientDisplayName);
      componentRef.setInput('bankDisplayLine', model.bankDisplayLine);
      componentRef.setInput('providerLabel', model.providerLabel);
      componentRef.setInput('agencyDisplay', model.agencyDisplay);
      componentRef.setInput('accountDisplay', model.accountDisplay);
      componentRef.setInput('digitableLine', model.digitableLine ?? '');
      componentRef.setInput('isBankSlip', !!model.isBankSlip);
      componentRef.setInput('logoUrl', model.logoUrl);
      componentRef.setInput('brandPrimaryColor', model.brandPrimaryColor);
      componentRef.setInput('domainTitle', model.domainTitle);

      componentRef.changeDetectorRef.detectChanges();
      await componentRef.instance.prepareForCapture();
      componentRef.changeDetectorRef.detectChanges();

      const root = componentRef.instance.exportRoot?.nativeElement;
      if (!root) {
        throw new Error('Payment receipt export root element is missing');
      }

      await new Promise<void>(resolve => {
        requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
      });

      if (debug) {
        this.openInNewTab(root);
      } else {
        await downloadElementAsPng(root, filename, {
          googleFontEmbed: {family: 'Inter', weights: [400, 600, 800]},
        });
      }
    } finally {
      this.appRef.detachView(componentRef.hostView);
      componentRef.destroy();
      host.remove();
    }
  }

  /** Opens a rendered export card in a new tab (styles copied from the current page). */
  private openInNewTab(root: HTMLElement): void {
    const tab = window.open('', '_blank');
    if (!tab) {
      throw new Error('Could not open debug tab (popup blocked?)');
    }

    const headStyles = Array.from(
      document.head.querySelectorAll('style, link[rel="stylesheet"]'),
    )
      .map(el => el.outerHTML)
      .join('');

    tab.document.open();
    tab.document.write(`<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
${headStyles}
<style>body{margin:16px;background:#f5f5f5;}</style>
</head>
<body>
${root.outerHTML}
</body>
</html>`);
    tab.document.close();
  }
}
